import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import surHeaderHeight from "@tSurya/Handlers/SurHeaderHeightHandler";
import { COMMON, TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    linkLabel = "linkLabel",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    enterLink = "enterLink",
    showCloseButton = "showCloseButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.linkLabel]: string;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.enterLink]: LinkFieldValue;
        [fields.showCloseButton]: boolean;
    };
}

export interface BookAppointmentStyle {
    outerWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    headingText?: InjectableCss;
    ctaWrapper?: InjectableCss;
    ctaLink?: LinkPresentationProps;
    ctaChevron?: InjectableCss;
    closeButtonWrapper?: InjectableCss;
}

export const bookAppointmentStyle: BookAppointmentStyle = {
    contentWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 12px;
            padding: 6px 0;
            text-align: center;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.announcementBar.fontWeight};
            font-size: ${TYPOGRAPHY.announcementBar.fontSize};
            line-height: ${TYPOGRAPHY.announcementBar.lineHeight};
            text-transform: ${TYPOGRAPHY.announcementBar.textTransform};
            letter-spacing: ${TYPOGRAPHY.announcementBar.letterSpacing || "0"};
            max-width: ${COMMON.width.content}px;
            margin: 0 auto;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: calc(100% - 46px);
                            padding: 6px 0;
                            font-size: ${TYPOGRAPHY.announcementBar.mobileFontSize};
                        `,
                        css`
                            max-width: calc(100% - 46px);
                            padding: 6px 0;
                            font-size: ${TYPOGRAPHY.announcementBar.mobileFontSize};
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    ctaWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;
        `,
    },
    ctaChevron: {
        css: css`
            display: flex;
            align-items: center;
            width: 12px;
            height: 12px;
        `,
    },
    closeButtonWrapper: {
        css: css`
            padding: 0;
            background: transparent;
            line-height: 0;
            min-height: 16px;
            border: 0;
            position: absolute;
            right: 1%;
            top: 50%;
            transform: translateY(-50%);
            cursor: pointer;
            span {
                height: 16px;
                width: 16px;
            }
            &:hover {
                background: transparent;
            }
        `,
    },
};

const mapDispatchToProps = {};

const styles = bookAppointmentStyle;

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export const BookAppointmentOffers: FC<Props> = ({ fields }) => {
    const [isClose, setIsClose] = useState(false);

    const closeHandler = () => {
        setIsClose(true);
    };

    const enteredLink = useGetLink(fields.enterLink);
    const bgColor = fields.backgroundColor || WIDGETS.announcementBar.defaultBackground;
    const txtColor = fields.textColor || WIDGETS.announcementBar.defaultTextColor;
    const hasCtaLabel = !!fields.linkLabel;
    const showClose = fields.showCloseButton;

    const outerWrapperStyles: InjectableCss = {
        css: css`
            background-color: ${bgColor};
            color: ${txtColor};
            position: relative;
            ${isClose && "display: none;"}
        `,
    };

    const textLinkStyles: LinkPresentationProps = {
        typographyProps: {
            css: css`
                color: ${txtColor} !important;
                text-decoration: underline;
                font-weight: ${TYPOGRAPHY.weight.baseBold};
            `,
        },
    };

    const legacyLinkStyles: LinkPresentationProps = {
        typographyProps: {
            css: css`
                color: ${txtColor} !important;
            `,
        },
    };

    return (
        <StyledWrapper {...outerWrapperStyles}>
            <StyledWrapper {...styles.contentWrapper}>
                {hasCtaLabel ? (
                    <>
                        <Typography
                            as="span"
                            css={css`
                                color: ${txtColor};
                            `}
                        >
                            {fields.heading}
                        </Typography>
                        <StyledWrapper {...styles.ctaWrapper}>
                            <Link href={enteredLink.url} {...textLinkStyles}>
                                {fields.linkLabel}
                            </Link>
                            <StyledWrapper {...styles.ctaChevron}>
                                <Icon src={ChevronRight} color={txtColor} size={12} />
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                ) : (
                    <Link href={enteredLink.url} {...legacyLinkStyles}>
                        {fields.heading}
                    </Link>
                )}
                {showClose && (
                    <Button {...styles.closeButtonWrapper} onClick={closeHandler} data-test-selector="Close Button" aria-label="Close announcement">
                        <Icon src={X} color={txtColor} size={16} />
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(BookAppointmentOffers),
    definition: {
        group: "Basic",
        displayName: "Book Appointment Offers",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading Text",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.linkLabel,
                displayName: "Link Label",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                tooltip: "If set, heading becomes plain text and this label is shown as a separate underlined CTA link with a chevron.",
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Leave empty to use brand default.",
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Leave empty to use brand default.",
            },
            {
                name: fields.enterLink,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: fields.showCloseButton,
                displayName: "Show Close Button",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
